import { useInterviewAgent } from '@/store/InterviewAgent';
import { Box, Flex, Stack, Steps, FlexProps } from '@chakra-ui/react';

type QuestionStepperProps = FlexProps;

export default function QuestionStepper({ ...props }: QuestionStepperProps) {
  const { questions, currentQuestionId } = useInterviewAgent();

  const currentQuestionIndex = questions.findIndex((q) => q.questionId === currentQuestionId);
  const currentStep = currentQuestionIndex >= 0 ? currentQuestionIndex : 0;

  const maxVisibleSteps = 6;
  let visibleQuestions = questions;
  let adjustedCurrentStep = currentStep;
  let startIndex = 0;

  if (questions.length > maxVisibleSteps) {
    // Center the current step in the visible window when possible
    startIndex = Math.max(
      0,
      Math.min(currentStep - Math.floor(maxVisibleSteps / 2), questions.length - maxVisibleSteps)
    );
    visibleQuestions = questions.slice(startIndex, startIndex + maxVisibleSteps);
    adjustedCurrentStep = currentStep - startIndex;
  }

  const steps = visibleQuestions.map((question, index) => {
    const actualQuestionNumber = startIndex + index + 1;

    return {
      title: `Question ${actualQuestionNumber}`,
      completed: question.completed
    };
  });

  return (
    <Flex direction="column" align="center" justify="center" {...props}>
      <Steps.Root
        colorPalette="blue"
        color="primary.900"
        step={adjustedCurrentStep}
        count={steps.length}
      >
        <Steps.List>
          {steps.map((item, index) => {
            const isCompleted = item.completed;
            const isCurrent = index === adjustedCurrentStep;
            const isPast = index < adjustedCurrentStep;

            return (
              <Steps.Item key={index} index={index} title={item.title}>
                <Stack align="center" gap="8px">
                  <Box
                    w="12px"
                    h="12px"
                    borderRadius="full"
                    border="2px solid"
                    bg={
                      isCompleted
                        ? 'primary.900'
                        : isCurrent
                        ? 'primary.0'
                        : isPast
                        ? 'primary.900'
                        : 'neutrals.500'
                    }
                    borderColor={isCompleted || isCurrent ? 'primary.900' : 'neutrals.500'}
                    color={isCompleted ? 'white' : isCurrent ? 'primary.900' : 'neutrals.500'}
                  />
                  <Steps.Title
                    fontSize="16px"
                    color={isCompleted || isCurrent ? 'neutrals.900' : 'neutrals.500'}
                    fontWeight={isCurrent ? 'semibold' : 'normal'}
                  >
                    {item.title}
                  </Steps.Title>
                </Stack>
                <Steps.Separator bg={isCompleted || isPast ? 'primary.900' : 'neutrals.500'} />
              </Steps.Item>
            );
          })}
        </Steps.List>
      </Steps.Root>
    </Flex>
  );
}
